// components/NewSaleForm.js
import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Tab, Box, Typography, Button } from '@mui/material';
import { SectionTitle, FormContainer, cores } from '../../../styles/styleUtils';
import CustomerData from '../components/CustomerData';
import EquipmentData from './EquipmentData';
import ServiceData from './ServiceData';
import SeguroData from './SeguroData';
import PaymentDetails from './PaymentDetails';
import SellerInfo from './SellerInfo';
import SaleSummary from './SaleSummary';
import AddressData from './AddressData';
import ImportaDocumento from '../components/ImportaDados/ImportaDocumento';
import ModalLoading from '../../ModalLoading';
import { getUnoProposta } from '../../api';
import calculaEncargos from '../funcs/calculaEncargos';

// Botão de avanço/confirmação do Wizard em laranja Marcki (o fundo escuro do
// painel agora vem do FormContainer compartilhado — ver styleUtils.cores).
const btnLaranjaSx = {
  backgroundColor: cores.laranja,
  '&:hover': { backgroundColor: cores.laranjaEscuro },
};


const hojeNoFusoBrasileiro = () => {
  const now = new Date();
  const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // zera hora local
  return localDate;
};

// Etapas do Wizard de lançamento de venda
const WIZARD_STEPS = ['Cliente', 'Equipamento', 'Pagamento', 'Vendedor', 'Resumo'];
const ULTIMO_PASSO = WIZARD_STEPS.length - 1;

// Componente auxiliar para os painéis de conteúdo das abas
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {' '}
          {/* Adiciona um padding ao redor do conteúdo */}
          {children}
        </Box>
      )}
    </div>
  );
}

const NewSaleForm = React.forwardRef((props, ref) => {
  const [formData, setFormData] = useState(
    props.formData || {
      data: hojeNoFusoBrasileiro(),
      tipoCliente: 'F',
      nomeCliente: '',
      cpf: '',
      nascimento: '',
      telefone: '',
      sexo: '',
      cep: '',
      endereco: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      email: '',
      codigo: '',
      kit: '',
      ncm: '',
      valor: '',
      formaPagamento: '',
      quantidadeParcelas: '',
      periodicidade: '',
      valorParcelas: '',
      dataPrimeiraParcela: hojeNoFusoBrasileiro(),
      manual: false,
      maquininha: '',
      vendedorAbertura: '',
      vendedorFechamento: '',
      unidade: '',
      formaAtendimento: '',
      valorKit: 0,
      valorServico: 0,
      valorSeguro: 0,
    }
  );
  const [openModal, setOpenModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [disableConfirm, setDisableConfirm] = useState(true);
  const [activeTab, setActiveTab] = useState(0); // 0 para a primeira aba, 1 para a segunda, etc.
  const [dadosImportados, setDadosImportados] = useState(false);
  const [step, setStep] = useState(0); // etapa atual do Wizard

  const handleProximo = () => setStep((s) => Math.min(s + 1, ULTIMO_PASSO));
  const handleVoltar = () => setStep((s) => Math.max(s - 1, 0));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === 'cep') {
      const newValue = value.replace(/\D/g, '');
      setFormData({
        ...formData,
        [name]: newValue,
      });
      return;
    }
    if (type === 'date' && event.target.value) {
      const newDate = new Date(`${value}T00:00:00`);
      setFormData({
        ...formData,
        [name]: newDate,
      });
      return;
    }
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleAutoCompleteChange = (idName, name, value) => {
    setModalLoading(true);
    setFormData({
      ...formData,
      [idName]: value?._id,
      [name]: value,
    });
    setModalLoading(false);
  };

  const handleCustomerTypeChange = (event) => {
    setFormData({
      ...formData,
      tipoCliente: event.target.value,
    });
  };

  useEffect(() => {
    if (formData.visualiza) return;
    if (formData.valor > 0 && formData.quantidadeParcelas) {
      const quantidade = Number(formData.quantidadeParcelas);
      const valorPrimeira = Number(formData.valorPrimeiraParcela || 0);
      const divisor = quantidade - (valorPrimeira > 0 ? 1 : 0);

      const newValorParcela =
        divisor > 0
          ? (Number(formData.valor) - valorPrimeira) / divisor
          : Number(formData.valor);

      setFormData((prev) => ({
        ...prev,
        valorParcelas: newValorParcela,
      }));
    }
  }, [formData.valor, formData.quantidadeParcelas, formData.valorPrimeiraParcela, formData.visualiza]);

  // valor liquido e encargos
  useEffect(() => {
    if (formData.visualiza) return;
    if (!formData.dataRecebimento) return;

    if (formData.financeiro?.length > 0) {
      formData.financeiro.forEach((item) => {
        const newEncargo = calculaEncargos(item.valor, formData, item);
        item.encargos = newEncargo;
        item.valorLiquido = item.valor - (newEncargo ?? 0);
      });
    }
  }, [
    formData.financeiro,
    formData.formaPagamento,
    formData.operadora,
    formData.antecipacaoAutomatica,
    formData.bancoEmissor,
    formData.dataRecebimento,
  ]);

  const handleSearchCEP = () => {
    const { cep } = formData;
    if (cep) {
      fetch(`https://viacep.com.br/ws/${cep}/json/`)
        .then((response) => response.json())
        .then((data) => {
          setFormData({
            ...formData,
            endereco: data.logradouro,
            complemento: data.complemento,
            bairro: data.bairro,
            cidade: data.localidade,
            estado: data.estado,
            estadoSigla: data.uf,
          });
        })
        .catch((error) => {
          alert('CEP não encontrado');
          console.error('Erro ao buscar o cep');
        });
    } else {
      alert('Por favor, digite um CEP.');
    }
  };

  const calcularDataParcela = (dataInicial, indice, periodicidade) => {
    const data = new Date(dataInicial);

    for (let i = 0; i < indice; i += 1) {
      if (periodicidade === 'quinzenal') {
        data.setDate(data.getDate() + 15);
      } else if (periodicidade === 'bimestral') {
        data.setMonth(data.getMonth() + 2);
      } else {
        // Para uma única parcela ou quando não há periodicidade informada,
        // mantém a primeira data. Para parcelamentos, "mensal" é o padrão.
        data.setMonth(data.getMonth() + 1);
      }
    }

    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  };

  const construirFinanceiroFallback = (dados) => {
    const total = Number(dados.valor || 0);
    const quantidade = Number(dados.quantidadeParcelas || 0);
    let primeiraData = null;
    if (dados.dataPrimeiraParcela) {
      if (
        typeof dados.dataPrimeiraParcela === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(dados.dataPrimeiraParcela)
      ) {
        primeiraData = new Date(`${dados.dataPrimeiraParcela}T00:00:00`);
      } else {
        primeiraData = new Date(dados.dataPrimeiraParcela);
      }
    }

    if (
      !(total > 0) ||
      !(quantidade > 0) ||
      !primeiraData ||
      Number.isNaN(primeiraData.getTime())
    ) {
      return [];
    }

    const primeiraInformada = Number(dados.valorPrimeiraParcela || 0);
    const usarPrimeiraDiferente = primeiraInformada > 0 && quantidade > 1;

    const primeira = usarPrimeiraDiferente
      ? primeiraInformada
      : total / quantidade;

    const restante = quantidade > 1
      ? (total - primeira) / (quantidade - 1)
      : total;

    const financeiro = Array.from({ length: quantidade }, (_, index) => ({
      parcela: index + 1,
      valor: Number((index === 0 ? primeira : restante).toFixed(2)),
      data: calcularDataParcela(
        primeiraData,
        index,
        dados.periodicidade
      ),
      formaPagamento: dados.formaPagamento,
    }));

    const soma = financeiro.reduce((acc, item) => acc + Number(item.valor || 0), 0);
    const diferenca = Number((total - soma).toFixed(2));

    if (financeiro.length > 0 && Math.abs(diferenca) >= 0.01) {
      const ultima = financeiro.length - 1;
      financeiro[ultima].valor = Number(
        (financeiro[ultima].valor + diferenca).toFixed(2)
      );
    }

    return financeiro;
  };

  const handleConfirm = () => {
    const total = Number(formData.valor || 0);
    let financeiroFinal = Array.isArray(formData.financeiro)
      ? formData.financeiro
      : [];

    // Trava defensiva: uma venda com valor positivo nunca deve ser enviada
    // sem ao menos uma parcela financeira. Se algum useEffect tiver perdido
    // o array, ele é reconstruído a partir dos dados visíveis do pagamento.
    if (total > 0 && financeiroFinal.length === 0) {
      financeiroFinal = construirFinanceiroFallback(formData);
    }

    if (total > 0 && financeiroFinal.length === 0) {
      window.alert(
        'Não foi possível gerar as parcelas financeiras desta venda. Volte à etapa Pagamento e confira quantidade de parcelas e data da primeira parcela.'
      );
      return;
    }

    const somaFinanceiro = financeiroFinal.reduce(
      (acc, item) => acc + Number(item.valor || 0),
      0
    );

    if (total > 0 && Math.abs(somaFinanceiro - total) > 0.01) {
      window.alert(
        `O valor das parcelas financeiras (R$ ${somaFinanceiro.toFixed(2).replace('.', ',')}) não confere com o valor total da venda (R$ ${total.toFixed(2).replace('.', ',')}). Revise a etapa Pagamento.`
      );
      return;
    }

    const dadosParaSalvar = {
      ...formData,
      financeiro: financeiroFinal,
    };

    props.onConfirm(dadosParaSalvar);
  };

  const handleCancel = () => {
    if (window.confirm('Tem certeza que deseja cancelar?')) {
      props.handleClose();
    }
  };

  const openImporta = () => {
    setOpenModal(true);
  };

  const closeImporta = () => {
    setOpenModal(false);
  };

  /*
  const onImportaDados = async (e) => {
    setModalLoading(true);
    const proposta = await getUnoProposta(e);
    setFormData({
      ...formData,
      idProposta: proposta._id,
      idCliente: proposta.cliente._id,
      cliente: proposta.cliente,
      idKit: proposta.kit._id,
      kit: proposta.kit,
      idFormaPagamento: proposta.formapagamento._id,
      formaPagamento: proposta.formapagamento,
      formaAtendimento: proposta.origem.nome,
      valor: proposta.valor,
      vendedorAbertura: proposta.user2 ? proposta.user2 : proposta.user,
      idVendedorAbertura: proposta.user2 ? proposta.user2._id : proposta.user._id,
      vendedorFechamento: proposta.user,
      idVendedorFechamento: proposta.user._id,
      unidade: proposta.unidade,
    });
    setModalLoading(false);
  };
  */

  const onImportaDados = async (propostasSelecionadasPreview) => {
    // Garante que temos um array com pelo menos um item
    if (!propostasSelecionadasPreview || propostasSelecionadasPreview.length === 0) {
      return;
    }

    setModalLoading(true);

    try {
      const promises = propostasSelecionadasPreview.map((p) => getUnoProposta(p));
      const propostasCompletas = await Promise.all(promises);

      // Daqui em diante, usamos o array `propostasCompletas` que tem todos os dados.
      const novoFormData = {};
      const camposInconsistentes = [];
      const baseProposta = propostasCompletas[0]; // Usa a primeira como referência

      // 1. VERIFICAÇÃO DE CAMPOS PADRÃO
      const camposPadraoConfig = {
        idCliente: (p) => p.cliente?._id,
        cliente: (p) => p.cliente,
        idFormaPagamento: (p) => p.formapagamento?._id,
        formaPagamento: (p) => p.formapagamento,
        formaAtendimento: (p) => p.origem?.nome,
        idVendedorAbertura: (p) => (p.user2 ? p.user2._id : p.user._id),
        vendedorAbertura: (p) => (p.user2 ? p.user2 : p.user),
        idVendedorFechamento: (p) => p.user._id,
        vendedorFechamento: (p) => p.user,
        unidade: (p) => p.unidade,
        quantidadeParcelas: (p) => p.parcelas,
      };

      Object.keys(camposPadraoConfig).forEach((campo) => {
        const extrairValor = camposPadraoConfig[campo];
        let valorBase = extrairValor(baseProposta);
        let isConsistente = true;

        for (let i = 1; i < propostasCompletas.length; i += 1) {
          const propostaAtual = propostasCompletas[i];
          const valorAtual = extrairValor(propostaAtual);

          if (valorBase === undefined && valorAtual !== undefined) {
            valorBase = valorAtual;
          }

          if (
            valorBase !== undefined &&
            valorAtual !== undefined &&
            JSON.stringify(valorBase) !== JSON.stringify(valorAtual)
          ) {
            isConsistente = false;
            camposInconsistentes.push(campo);
            break;
          }
        }

        if (isConsistente) {
          novoFormData[campo] = valorBase;
        }
      });

      if (camposInconsistentes.length > 0) {
        alert(
          'Atenção! Os seguintes campos são diferentes entre as propostas e não foram importados:\n\n' +
            `- ${camposInconsistentes.join('\n- ')}` +
            '\n\nPor favor, preencha-os manualmente.'
        );
      }

      // 2. PROCESSAMENTO DOS CAMPOS ESPECÍFICOS POR TIPO DE PROPOSTA
      let valorKitTotal = 0;
      let valorServicoTotal = 0;
      let valorSeguroTotal = 0;

      novoFormData.idProposta = null;
      novoFormData.idKit = null;
      novoFormData.kit = null;
      novoFormData.idPropostaServico = null;
      novoFormData.servicos = null;
      novoFormData.idPropostaSeguro = null;
      novoFormData.negociacao = null;

      // Roteia por palavra-chave normalizada (sem acento, minúsculas) para
      // funcionar com os nomes novos dos produtos (Gerador On-Grid, Off-Grid,
      // Sistema Bombeamento, Personalizada...) e manter os antigos.
      const normalizarNome = (texto) =>
        (texto || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();

      propostasCompletas.forEach((proposta) => {
        const nomeProduto = normalizarNome(proposta.produto?.nome);

        if (nomeProduto.includes('servic')) {
          // Proposta de Serviços
          novoFormData.idPropostaServico = proposta._id;
          if (proposta.servicos) {
            novoFormData.servicos = proposta.servicos;
            novoFormData.kmRodado = proposta.kmrodado;
            novoFormData.subTotalServicos = proposta.subTotalServicos;
            novoFormData.valorDeslocamento = proposta.valorDeslocamento;
            novoFormData.valorIndicacao = proposta.valorIndicacao;
          }
          valorServicoTotal = proposta.valor || 0;
        } else if (nomeProduto.includes('seguro')) {
          // Seguro
          novoFormData.idPropostaSeguro = proposta._id;
          if (proposta.negociacao) {
            novoFormData.negociacao = proposta.negociacao;
            novoFormData.valorProjeto = proposta.valorprojeto;
          }
          valorSeguroTotal = proposta.valor || 0;
        } else {
          // Qualquer produto com KIT: On-Grid, Off-Grid, Bombeamento,
          // Personalizada, Grid-Zero ou a antiga "Proposta Comercial".
          novoFormData.idProposta = proposta._id;
          if (proposta.kit) {
            novoFormData.kit = proposta.kit;
            novoFormData.idKit = proposta.kit._id;
          } else {
            console.warn(
              `Proposta sem kit em proposta.kit (produto: ${proposta.produto?.nome}). Verificar onde o kit está armazenado.`
            );
          }
          valorKitTotal = proposta.valor || 0;
        }
      });

      novoFormData.valorKit = valorKitTotal;
      novoFormData.valorServico = valorServicoTotal;
      novoFormData.valorSeguro = valorSeguroTotal;

      // 3. CÁLCULO DO VALOR TOTAL
      novoFormData.valor = valorKitTotal + valorServicoTotal + valorSeguroTotal;

      // 4. ATUALIZAÇÃO DO ESTADO
      setFormData((prevFormData) => ({
        ...prevFormData,
        ...novoFormData,
      }));
    } catch (error) {
      console.error('Erro ao importar e processar dados das propostas:', error);
      alert('Ocorreu um erro ao buscar os dados completos das propostas. Verifique o console.');
    } finally {
      setModalLoading(false);
      setDadosImportados(true);
    }
  };

  // Quando aberto a partir de "Vendas não lançadas", o Wizard recebe as
  // propostas já escolhidas e roda o mesmo mapeamento do Importar Dados,
  // preenchendo os campos automaticamente ao abrir.
  useEffect(() => {
    if (props.propostasParaImportar && props.propostasParaImportar.length > 0) {
      onImportaDados(props.propostasParaImportar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation logic for disableConfirm
  useEffect(() => {
    const {
      idCliente,
      endereco,
      numero,
      bairro,
      cidade,
      estado,
      valor,
      idFormaPagamento,
      quantidadeParcelas,
      valorParcelas,
      dataPrimeiraParcela,
      idVendedorAbertura,
      idVendedorFechamento,
      unidade,
      formaAtendimento,
      periodicidade,
      dataRecebimento,
      idContaRecebimento,
      formaPagamento,
      idOperadora,
      idBandeira,
      idBancoEmissor,
      antecipacaoAutomatica,
    } = formData;

    // Check required fields
    const areRequiredFieldsFilled =
      idCliente &&
      endereco &&
      numero &&
      bairro &&
      cidade &&
      estado &&
      valor &&
      idFormaPagamento &&
      quantidadeParcelas &&
      valorParcelas &&
      dataPrimeiraParcela &&
      idVendedorAbertura &&
      idVendedorFechamento &&
      unidade &&
      formaAtendimento;

    let specificValidationsMet = true;

    // Condition: se quantidadeParcelas > 1 precisa ter periodicidade
    if (quantidadeParcelas > 1 && !periodicidade) {
      specificValidationsMet = false;
    }

    // Condition: se tiver dataRecebimento precisa ter idContaRecebimento
    if (dataRecebimento && !idContaRecebimento) {
      specificValidationsMet = false;
    }

    // Condition: verificar formData.formaPagamento.tipo se for = 'cartao' precisa ter idOperadora e idBandeira
    if (dataRecebimento && formaPagamento && formaPagamento.tipo === 'cartao') {
      if (!idOperadora || !idBandeira) {
        specificValidationsMet = false;
      }
    }

    // Condition: se formData.formaPagamento.tipo se for = 'boleto' precisa ter idBancoEmissor e antecipacaoAutomatica
    if (dataRecebimento && formaPagamento && formaPagamento.tipo === 'boleto') {
      if (!idBancoEmissor || antecipacaoAutomatica === undefined) {
        // Check for undefined as false is a valid boolean
        specificValidationsMet = false;
      }
    }

    // Condition: se formData.formaPagamento.tipo se for = 'boleto' precisa ter idBancoEmissor e antecipacaoAutomatica
    if (
      dataRecebimento &&
      formaPagamento &&
      formaPagamento.tipo === 'cartao' &&
      formaPagamento.nome.includes('Parcelado')
    ) {
      if (!antecipacaoAutomatica || antecipacaoAutomatica === undefined || antecipacaoAutomatica === '') {
        // Check for undefined as false is a valid boolean
        specificValidationsMet = false;
      }
    }

    setDisableConfirm(!(areRequiredFieldsFilled && specificValidationsMet));
  }, [formData]); // Re-run validation when formData changes

  useEffect(() => {
    const valorTotal = (Number(formData.valorKit) || 0) + (Number(formData.valorServico) || 0) + (Number(formData.valorSeguro) || 0);
    setFormData((prev) => ({ ...prev, valor: valorTotal }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.valorKit, formData.valorServico, formData.valorSeguro]);

  const financeiroResumo = Array.isArray(formData.financeiro) ? formData.financeiro : [];
  const totalVendaCentavos = Math.round((Number(formData.valor || 0) + Number.EPSILON) * 100);
  const totalFinanceiroCentavos = financeiroResumo.reduce(
    (acc, item) => acc + Math.round((Number(item?.valor || 0) + Number.EPSILON) * 100),
    0
  );
  const financeiroConciliado =
    totalVendaCentavos > 0 &&
    financeiroResumo.length > 0 &&
    totalFinanceiroCentavos === totalVendaCentavos;

  return (
    <Modal
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <FormContainer>
        {/* Indicador de etapas do Wizard */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, flexWrap: 'wrap' }}>
          {WIZARD_STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <Typography
                sx={{
                  fontSize: '0.9em',
                  fontWeight: i === step ? 'bold' : 'normal',
                  color: i === step ? 'white' : 'rgba(255,255,255,0.6)',
                  borderBottom: i === step ? `2px solid ${cores.laranja}` : '2px solid transparent',
                  pb: '4px',
                  px: 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {`${i + 1}. ${label}`}
              </Typography>
              {i < ULTIMO_PASSO && (
                <Box sx={{ width: 24, height: '2px', backgroundColor: 'rgba(255,255,255,0.4)', mx: 0.5 }} />
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* Passo 1 — Cliente */}
        {step === 0 && (
          <>
            <CustomerData
              formData={formData}
              onCustomerTypeChange={handleCustomerTypeChange}
              onOpen={openImporta}
              disabled={formData.visualiza}
              onInputChange={handleInputChange}
              onAutoCompleteChange={handleAutoCompleteChange}
            />
            {formData.cliente && (
              <AddressData
                formData={formData}
                onInputChange={handleInputChange}
                onSearchCEP={handleSearchCEP}
                setFormData={setFormData}
              />
            )}
          </>
        )}

        {/* Passo 2 — Equipamento / Serviços / Seguro */}
        {step === 1 && formData.cliente && (
          <Box sx={{ width: '100%', mt: 1 }}>
            {/* O container das abas. Pode ter um fundo para o contraste da letra branca */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="Abas de dados da venda"
                centered // <-- 1. CENTRALIZA AS ABAS NA BARRA
                sx={{
                  // <-- 2. APLICA ESTILOS PARA A COR BRANCA
                  // Alvo: Todos os botões de Aba dentro deste componente
                  '& .MuiTab-root': {
                    color: 'white', // Letra branca para abas inativas
                  },
                  // Alvo: O indicador (a linha) da aba ativa
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'white', // Linha indicadora branca
                  },
                  // Alvo: A aba que está selecionada (opcional, mas bom para garantir)
                  '& .Mui-selected': {
                    color: 'white !important', // Garante que a aba ativa também seja branca
                    fontWeight: 'bold', // Coloca a aba ativa em negrito para dar destaque
                  },
                }}
              >
                <Tab label="Equipamento" id="simple-tab-0" aria-controls="simple-tabpanel-0" />
                <Tab label="Serviços" id="simple-tab-1" aria-controls="simple-tabpanel-1" />
                <Tab label="Seguro" id="simple-tab-2" aria-controls="simple-tabpanel-2" />
              </Tabs>
            </Box>

            {/* O conteúdo que aparece para cada aba */}
            <TabPanel value={activeTab} index={0}>
              <EquipmentData
                formData={formData}
                dadosImportados={dadosImportados}
                onInputChange={handleInputChange}
                onAutoCompleteChange={handleAutoCompleteChange}
                setFormData={setFormData}
              />
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <ServiceData formData={formData} dadosImportados={dadosImportados} setFormData={setFormData} />
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              <SeguroData formData={formData} dadosImportados={dadosImportados} setFormData={setFormData} />
            </TabPanel>
          </Box>
        )}

        {/* Passo 3 — Pagamento */}
        {step === 2 && formData.cliente && (
          <>
            <SectionTitle>Faturamento</SectionTitle>
            <PaymentDetails
              formData={formData}
              onInputChange={handleInputChange}
              onAutoCompleteChange={handleAutoCompleteChange}
              setFormData={setFormData}
            />
          </>
        )}

        {/* Passo 4 — Vendedor */}
        {step === 3 && formData.cliente && (
          <>
            <SectionTitle>Vendedor</SectionTitle>
            <SellerInfo
              formData={formData}
              onInputChange={handleInputChange}
              onAutoCompleteChange={handleAutoCompleteChange}
              disabled={formData.visualiza}
            />
          </>
        )}

        {/* Passo 5 — Resumo e conferência final */}
        {step === 4 && formData.cliente && (
          <>
            <SectionTitle>Resumo da Venda</SectionTitle>
            <SaleSummary formData={formData} />
          </>
        )}

        {/* Navegação do Wizard */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
          <Button
            onClick={handleVoltar}
            disabled={step === 0}
            variant="outlined"
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)' }}
          >
            Voltar
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleCancel}
              variant="outlined"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)' }}
            >
              Cancelar
            </Button>
            {step < ULTIMO_PASSO ? (
              <Button
                onClick={handleProximo}
                disabled={step === 0 && !formData.cliente}
                variant="contained"
                sx={btnLaranjaSx}
              >
                {step === ULTIMO_PASSO - 1 ? 'Revisar' : 'Próximo'}
              </Button>
            ) : (
              <Button
                onClick={handleConfirm}
                disabled={disableConfirm || !financeiroConciliado}
                variant="contained"
                sx={btnLaranjaSx}
              >
                Confirmar Venda
              </Button>
            )}
          </Box>
        </Box>

        {openModal && (
          <ImportaDocumento open={openModal} handleClose={closeImporta} onSelect={onImportaDados} type={'propostas'} />
        )}
        {modalLoading && <ModalLoading open={modalLoading} />}
      </FormContainer>
    </Modal>
  );
});

/*
AddProposta.propTypes = {
  produto: PropTypes.object,
  onConfirm: PropTypes.func,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  onClose: PropTypes.func,
}
*/

export default NewSaleForm;